import { FormikProps, useFormik } from 'formik'
import React, { ReactNode, useState } from 'react'
import ModalComponent from '..'
import { Button } from '../../button'
import { TextInput } from '../../inputs/textInput'

import { ExportReportSchema } from '../../../validation/validate'

import { format } from 'date-fns'
import { ObjectSchema } from 'yup'
import { parseDate } from '../../../helpers'
import {
  useDownloadReportsMutation,
  useExportReportsMutation,
} from '../../../redux-toolkits/transactions/transactions.slice'
import { ReportType } from '../../../redux-toolkits/reports/reports.type'
import Icon from '../../icon'
import { DateRangePicker } from '../../inputs/dateRange'
import Dropdown from '../../inputs/dropdown'
import { TabNav } from '../../nav/tabNav'

const periods = [
  { name: 'Today', value: 'today' },
  { name: 'Yesterday', value: 'yesterday' },
  { name: 'This week', value: 'this_week' },
  { name: 'This month', value: 'this_month' },
  { name: 'This year', value: 'this_year' },
  { name: 'Last month', value: 'last_month' },
  { name: 'Last week', value: 'last_week' },
  { name: 'Last year', value: 'last_year' },
  { name: 'Inception till date', value: 'inception_to_date' },
]

export const ExportDataModal: React.FC<{
  open: boolean
  setOpen: (cal: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: () => void
  extraValues?: {
    [key: string]: string | boolean | undefined
  }
  title: string
  reportType: ReportType
  children?: (form: FormikProps<any>) => ReactNode
  schema?: ObjectSchema<any>
  fulfilmentCentre?: string
}> = ({
  open,
  setOpen,
  onCloseCallback = () => {},
  onSuccessCallback = () => {},
  extraValues = {},
  title,
  reportType,
  fulfilmentCentre = '',
  children,
  schema,
}) => {
  const [actionType, setActionType] = useState<'export' | 'download'>('export')

  const [exportReport, { isLoading: exporting }] = useExportReportsMutation()
  const [downloadReport, { isLoading: downloading }] =
    useDownloadReportsMutation()

  const generalForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      startDate: '',
      endDate: '',
      isCustomDateRange: false,
      filter: '',
      reportType: reportType,
      fulfilmentCentre,
      ...extraValues,
    },
    validationSchema: schema
      ? ExportReportSchema.concat(schema)
      : ExportReportSchema,
    onSubmit: async (values) => {
      const params = Object.entries(values).reduce(
        (acc: Record<string, string | boolean>, [key, value]) => {
          if (value !== '') acc[key] = value
          return acc
        },
        {} as Record<string, string>,
      )

      if (actionType === 'export') {
        const resp = await exportReport(params)

        if (resp.data?.success) {
          onSuccessCallback()
          generalForm.resetForm()
          setOpen(false)
          onCloseCallback()
        }
      } else {
        const resp = await downloadReport(params)

        if (resp.data?.success) {
          onSuccessCallback()
          generalForm.resetForm()
          setOpen(false)
          onCloseCallback()
        }
      }
    },
  })
  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        generalForm.resetForm()
        onCloseCallback()
      }}
      title={`${title}`}
      childrenClassName="md:!max-w-[420px]"
    >
      <form className="flex flex-col gap-4" onSubmit={generalForm.handleSubmit}>
        <TabNav
          activeTab={generalForm.values.isCustomDateRange ? 'date' : 'period'}
          setActiveTab={(tab) => {
            generalForm.setFieldValue('isCustomDateRange', tab === 'date')

            if (tab === 'period') {
              generalForm.setFieldValue('startDate', '')
              generalForm.setFieldValue('endDate', '')
            } else {
              generalForm.setFieldValue('filter', '')
            }
          }}
          tabs={[
            { name: 'Period', id: 'period' },
            { name: 'Date Range', id: 'date' },
          ]}
          type="switcher"
          size="regular"
          className="w-full"
        />

        {generalForm.values.isCustomDateRange ? (
          <Dropdown
            menuButton={
              <TextInput
                value={`${
                  generalForm.values.startDate
                    ? format(generalForm.values.startDate!, 'dd MMM, yyyy')
                    : 'Start date'
                } - ${
                  generalForm.values.endDate
                    ? format(generalForm.values.endDate!, 'dd MMM, yyyy')
                    : 'End date'
                }`}
                title="Date range"
                hasIcon={true}
                iconSvg="calendar"
                onBlur={() => {}}
                onChange={() => {}}
              />
            }
            menuClassName="!min-w-max !border-[0px]"
            customMenuItems={
              <DateRangePicker
                required
                animate
                navLayout="around"
                mode="range"
                selected={{
                  from:
                    parseDate(generalForm.values.startDate)?.toDate() ??
                    undefined,
                  to:
                    parseDate(generalForm.values.endDate)?.toDate() ??
                    undefined,
                }}
                onSelect={(selected) => {
                  if (selected?.from && selected?.to) {
                    generalForm.setFieldValue(
                      'startDate',
                      format(selected?.from, 'yyyy-MM-dd'),
                    )
                    generalForm.setFieldValue(
                      'endDate',
                      format(selected?.to, 'yyyy-MM-dd'),
                    )
                  }
                }}
                numberOfMonths={2}
                disabled={{ after: new Date() }}
              />
            }
          />
        ) : (
          <Dropdown
            menuClassName="!max-h-[200px]"
            menuButton={
              <TextInput
                type="text"
                name="filter"
                placeholder="Period"
                title="Period"
                value={
                  periods.find((i) => i.value === generalForm.values.filter)
                    ?.name
                }
                onBlur={generalForm.handleBlur}
                onChange={generalForm.handleChange}
                errors={
                  generalForm.touched.filter && generalForm.errors.filter
                    ? generalForm.errors.filter
                    : ''
                }
                hasIcon={false}
                extra={
                  <Icon
                    id={'left-caret'}
                    width={16}
                    height={16}
                    className="-rotate-90 text-sec-black"
                  />
                }
              />
            }
            menuItems={periods.map((itm) => ({
              ...itm,
              onClick: () => {
                generalForm.setFieldValue(
                  'filter',
                  itm.value === 'all' ? undefined : (itm.value as string),
                )
              },
            }))}
          />
        )}

        {children ? children(generalForm) : undefined}

        <div className="flex flex-col mt-10 sticky bottom-0 bg-white">
          <Button
            label="Export"
            type="submit"
            onClick={() => {
              setActionType('export')
            }}
            className=""
            disabled={exporting}
            loading={exporting}
            primary
          />

          <Button
            label="Download to device"
            type="submit"
            onClick={() => {
              setActionType('download')
            }}
            className=""
            disabled={downloading}
            loading={downloading}
            primary
            variant="plain"
          />
        </div>
      </form>
    </ModalComponent>
  )
}
